import { BackendHttpError, BackendService, EventStreamProtocolError } from 'services/BackendService'

import type { ChatStreamEvent, components } from '@startech/openapi'
import type { AgentMessage } from 'modules/coaching/types'

type GetConversationResponse = components['schemas']['GetConversationResponse']
type ListConversationsResponse = components['schemas']['ListConversationsResponse']
type ListMessagesResponse = components['schemas']['ListMessagesResponse']
type ActiveChatTurnResponse = components['schemas']['ActiveChatTurnResponse']
type CancelChatTurnResponse = components['schemas']['CancelChatTurnResponse']
type ChatTurn = components['schemas']['ChatTurn']
type CreateChatTurnRequestBody = components['schemas']['CreateChatTurnRequestBody']

export const createChatTurn = async (
  parameters: CreateChatTurnRequestBody,
  signal?: AbortSignal
): Promise<ChatTurn> => {
  return BackendService.POST<ChatTurn>('chat-turns', parameters, { signal })
}

export const fetchActiveChatTurn = async (
  conversationId: string,
  signal?: AbortSignal
): Promise<ChatTurn | undefined> => {
  const response = await BackendService.GET<ActiveChatTurnResponse>('chat-turns/active', {
    query: { conversationId },
    signal,
  })
  return response.turn ?? undefined
}

export type SequencedChatStreamEvent = {
  sequence: number
  event: ChatStreamEvent
}

export class ChatStreamProtocolError extends Error {
  override readonly name = 'ChatStreamProtocolError'
}

export const streamChatTurnEvents = async function* (
  turnId: string,
  after: number,
  signal?: AbortSignal
): AsyncGenerator<SequencedChatStreamEvent, void> {
  let cursor = after
  try {
    for await (const item of BackendService.EVENT_STREAM<ChatStreamEvent>(`chat-turns/${turnId}/events`, {
      query: { after },
      signal,
    })) {
      const sequence = Number(item.id)
      if (!Number.isSafeInteger(sequence)) {
        throw new ChatStreamProtocolError('Chat stream returned an invalid event sequence')
      }
      if (sequence <= cursor) {
        continue
      }
      if (sequence !== cursor + 1) {
        throw new ChatStreamProtocolError(`Chat stream event gap after sequence ${cursor}`)
      }
      cursor = sequence
      yield { sequence, event: item.data }
    }
  } catch (error) {
    if (error instanceof EventStreamProtocolError) {
      throw new ChatStreamProtocolError(error.message)
    }
    throw error
  }
}

export const cancelChatTurn = async (turnId: string): Promise<ChatTurn> => {
  const response = await BackendService.POST<CancelChatTurnResponse>(`chat-turns/${turnId}/cancel`)
  return response.turn
}

export const getChatApiErrorStatus = (error: unknown): number | undefined => {
  return error instanceof BackendHttpError ? error.status : undefined
}

export const fetchUserConversations = async (
  page: number,
  pageSize: number,
  signal?: AbortSignal
): Promise<ListConversationsResponse> => {
  return BackendService.GET<ListConversationsResponse>('conversations', {
    query: { page, pageSize },
    signal,
  })
}

export const fetchConversationById = async (
  conversationId: string,
  signal?: AbortSignal
): Promise<GetConversationResponse | undefined> => {
  try {
    return await BackendService.GET<GetConversationResponse>(`conversations/${conversationId}`, {
      auth: 'optional',
      signal,
    })
  } catch (error) {
    if (error instanceof Error && error.message.includes('not found')) {
      return undefined
    }
    throw error
  }
}

export const fetchMessagesForConversation = async (
  conversationId: string,
  signal?: AbortSignal
): Promise<AgentMessage[]> => {
  const result = await BackendService.GET<ListMessagesResponse>(`conversations/${conversationId}/messages`, {
    auth: 'optional',
    signal,
  })

  return result.data.map(row => ({
    id: row.id,
    role: row.role,
    contents: row.content,
    timestamp: new Date(row.created_at ?? Date.now()),
    interactionId: row.interaction_id ?? undefined,
  }))
}

export const updateConversationSharing = async (
  conversationId: string,
  isPublic: boolean
): Promise<components['schemas']['UpdateConversationSharingResponse']> => {
  return BackendService.PATCH<components['schemas']['UpdateConversationSharingResponse']>(
    `conversations/${conversationId}/sharing`,
    { isPublic }
  )
}
